/**
 * BrandPicker (Iter 9.39a)
 *
 * Brand search + suggest input that pulls from /api/host/brands/visible
 * (admin-published brands + this host's own pending submissions).
 *
 * If the brand the host wants is missing, they can submit a new brand which
 * goes into the admin review queue. The host immediately sees it as
 * "pending review" and can use it locally; other hosts won't see it until
 * an admin promotes it.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Search, Tag, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandPicker({
  value,                 // selected brand_id (string) | null
  onChange,              // (brand_id, brand_obj) => void
  category = "style",
  testid = "brand-picker",
}) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/host/brands/visible?category=${encodeURIComponent(category)}`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setBrands(d.brands || []); }
    } finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const filtered = brands.filter((b) =>
    !search.trim() || (b.name || "").toLowerCase().includes(search.trim().toLowerCase()),
  );
  const exact = brands.find((b) => (b.name || "").trim().toLowerCase() === search.trim().toLowerCase());
  const canSubmitNew = !!search.trim() && !exact;

  const submitNew = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/host/brands/submit`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ name: search.trim(), category }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Submit failed");
      toast.success(`"${search.trim()}" submitted for admin review`);
      const newBrand = d.brand;
      onChange && onChange(newBrand.brand_id, newBrand);
      setSearch("");
      load();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally { setSubmitting(false); }
  };

  const selected = brands.find((b) => b.brand_id === value);

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brand or type a new one…"
          className="pl-8"
          data-testid={`${testid}-search`}
        />
      </div>

      {selected && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 border border-purple-100">
          {selected.logo_url ? (
            <img src={selected.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
          ) : (
            <Tag className="w-5 h-5 text-purple-600" />
          )}
          <span className="text-sm font-medium text-purple-900" data-testid={`${testid}-selected`}>{selected.name}</span>
          {/* Iter 9.40 Slice I.e - host never sees "pending"; it's an admin-only concept. */}
          <div className="flex-1" />
          <button type="button" onClick={() => onChange(null, null)} className="text-xs text-gray-500 hover:text-red-500" data-testid={`${testid}-clear`}>
            Clear
          </button>
        </div>
      )}

      <div className="max-h-44 overflow-y-auto border rounded-lg divide-y">
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-4 text-center text-xs text-gray-400">{search ? "No matches in master directory." : "No brands yet."}</div>
        ) : (
          filtered.slice(0, 30).map((b) => {
            const isSelected = b.brand_id === value;
            return (
              <button
                key={b.brand_id}
                type="button"
                onClick={() => onChange(b.brand_id, b)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${isSelected ? "bg-purple-50" : ""}`}
                data-testid={`${testid}-row-${b.brand_id}`}
              >
                {b.logo_url ? (
                  <img src={b.logo_url} alt="" className="w-6 h-6 rounded object-cover" />
                ) : (
                  <Tag className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-sm text-gray-800 flex-1">{b.name}</span>
                {isSelected && <Check className="w-4 h-4 text-purple-600" />}
              </button>
            );
          })
        )}
      </div>

      {canSubmitNew && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={submitNew}
          disabled={submitting}
          className="w-full justify-start text-purple-700 border-purple-200 hover:bg-purple-50"
          data-testid={`${testid}-submit-new`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          {submitting ? "Submitting…" : `Suggest new brand "${search.trim()}" (admin review)`}
        </Button>
      )}
    </div>
  );
}
